import React, { useState } from 'react';
import { Box, Button, Divider, IconButton, Menu, Tooltip } from '@mui/material';
import {
  MRT_ShowHideColumnsMenuItems,
  getDefaultColumnOrderIds,
} from 'material-react-table';

export const partitionColumnsByVisibility = (columns = []) => {
  const visible = [];
  const hidden = [];

  columns.forEach((column) => {
    if (column?.getIsVisible?.() === false) {
      hidden.push(column);
      return;
    }
    visible.push(column);
  });

  return [...visible, ...hidden];
};

export const getVisibilitySortedColumns = (table) => {
  const allColumns = table?.getAllColumns?.() || [];
  const { columnOrder = [] } = table?.getState?.() || {};
  const hasGroupedColumns = allColumns.some(
    (column) => column?.columnDef?.columnDefType === 'group'
  );

  if (!columnOrder.length || hasGroupedColumns) {
    return partitionColumnsByVisibility(allColumns);
  }

  const leftColumns = table?.getLeftLeafColumns?.() || [];
  const centerColumns = table?.getCenterLeafColumns?.() || [];
  const rightColumns = table?.getRightLeafColumns?.() || [];
  const centerById = new Map(centerColumns.map((column) => [column.id, column]));
  const orderedCenterColumns = [];
  const seenCenterIds = new Set();

  columnOrder.forEach((columnId) => {
    const column = centerById.get(columnId);
    if (!column || seenCenterIds.has(column.id)) return;
    seenCenterIds.add(column.id);
    orderedCenterColumns.push(column);
  });

  centerColumns.forEach((column) => {
    if (seenCenterIds.has(column.id)) return;
    seenCenterIds.add(column.id);
    orderedCenterColumns.push(column);
  });

  return partitionColumnsByVisibility([
    ...leftColumns,
    ...orderedCenterColumns,
    ...rightColumns,
  ]);
};

// Material React Table's stock chooser follows the table's persisted column
// order. This wrapper keeps that order for drag/pin behavior, but presents a
// stable visible-first list so hidden schema fields do not interrupt the fields
// an operator is actively arranging.
const VisibleFirstColumnsButton = ({ table }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [hoveredColumn, setHoveredColumn] = useState(null);
  const {
    enableColumnOrdering,
    enableColumnPinning,
    enableHiding,
    icons: { ViewColumnIcon },
    localization,
    mrtTheme: { menuBackgroundColor },
  } = table.options;
  const { density } = table.getState();
  const sortedColumns = getVisibilitySortedColumns(table);
  const isNestedColumns = sortedColumns.some(
    (column) => column?.columnDef?.columnDefType === 'group'
  );

  const handleToggleAllColumns = (value) => {
    table
      .getAllLeafColumns()
      .filter((column) => column.columnDef.enableHiding !== false)
      .forEach((column) => column.toggleVisibility(value));
  };

  return (
    <>
      <Tooltip title={localization.showHideColumns}>
        <IconButton
          aria-label={localization.showHideColumns}
          onClick={(event) => setAnchorEl(event.currentTarget)}
        >
          <ViewColumnIcon />
        </IconButton>
      </Tooltip>
      {anchorEl ? (
        <Menu
          MenuListProps={{
            dense: density === 'compact',
            sx: { backgroundColor: menuBackgroundColor },
          }}
          anchorEl={anchorEl}
          disableScrollLock
          onClose={() => setAnchorEl(null)}
          open
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              p: '0.5rem',
              pt: 0,
            }}
          >
            {enableHiding ? (
              <Button
                disabled={!table.getIsSomeColumnsVisible()}
                onClick={() => handleToggleAllColumns(false)}
              >
                {localization.hideAll}
              </Button>
            ) : null}
            {enableColumnOrdering ? (
              <Button
                onClick={() => table.setColumnOrder(getDefaultColumnOrderIds(table.options, true))}
              >
                {localization.resetOrder}
              </Button>
            ) : null}
            {enableColumnPinning ? (
              <Button
                disabled={!table.getIsSomeColumnsPinned()}
                onClick={() => table.resetColumnPinning(true)}
              >
                {localization.unpinAll}
              </Button>
            ) : null}
            {enableHiding ? (
              <Button
                disabled={table.getIsAllColumnsVisible()}
                onClick={() => handleToggleAllColumns(true)}
              >
                {localization.showAll}
              </Button>
            ) : null}
          </Box>
          <Divider />
          {sortedColumns.map((column) => (
            <MRT_ShowHideColumnsMenuItems
              allColumns={sortedColumns}
              column={column}
              hoveredColumn={hoveredColumn}
              isNestedColumns={isNestedColumns}
              key={column.id}
              setHoveredColumn={setHoveredColumn}
              table={table}
            />
          ))}
        </Menu>
      ) : null}
    </>
  );
};

export default VisibleFirstColumnsButton;
